<script lang="ts">
	import type { BorderRadiusPosition } from '$lib/components/map/markers/types';
	import type { Coordinates, RouteType, RouteWithGeometry } from '$lib/components/map/types';
	import ButtonMarker from '$lib/components/map/markers/ButtonMarker.svelte';
	import RouteMarkerContent from '$lib/components/map/markers/markerContent/routeMarkerContent/RouteMarkerContent.svelte';
	import { RouteTypeMap } from '$lib/components/route/types';
	import * as c from '$lib/i18n/computedMessages';
	import { getDurationHoursAndMinutes } from '$lib/utils/getDurationHoursAndMinutes';
	import * as m from '$paraglide/messages';

	interface Props {
		routes?: RouteWithGeometry[];
		selectedRoute: number;
		displayOnlySelected?: boolean;
		onclick?: (routeIndex: number) => void;
	}

	const { routes = [], selectedRoute, displayOnlySelected = false, onclick }: Props = $props();

	const MAX_PART: number = 4;

	type MarkerData = {
		index: number;
		ariaLabel: string;
		hours: number;
		minutes: number;
		totalCost: { amount: number; currency: string };
		position: Coordinates;
		anchor: maplibregl.PositionAnchor;
		borderRadiusPosition: BorderRadiusPosition;
	};

	const markerAnchorPosition: maplibregl.PositionAnchor[] = [
		'top-left',
		'bottom-right',
		'bottom-right',
	];
	const markerBorderRadiusPosition: BorderRadiusPosition[] = [
		'topLeft',
		'bottomRight',
		'bottomRight',
	];

	function getTranslatedAriaLabel(types: RouteType) {
		if (types.length === 0) {
			return m.route__routemarkers__ariaLabel__default();
		}
		const type = RouteTypeMap[types[0]];
		return c.routeMarkersLabel(type);
	}

	const displayedRoutes = $derived(displayOnlySelected
		? routes.filter(route => route.index === selectedRoute)
		: routes);

	const markerData: MarkerData[] | undefined = $derived(displayedRoutes.length > 0
		? displayedRoutes.map((route) => {
			const percentage = (route.index + 1) / MAX_PART;
			const geometryInGeoJSON = route.geometry;
			const indexForMarker = Math.round(geometryInGeoJSON.coordinates.length * percentage - 1);
			const [lng, lat] = geometryInGeoJSON.coordinates[indexForMarker];
			const { hours, minutes } = getDurationHoursAndMinutes(route.duration);
			return {
				ariaLabel: getTranslatedAriaLabel(route.types),
				index: route.index,
				hours,
				minutes,
				totalCost: route.totalCost,
				position: { lng, lat },
				anchor: markerAnchorPosition[route.index],
				borderRadiusPosition: markerBorderRadiusPosition[route.index],
			};
		})
		: undefined);
</script>

{#if markerData}
	{#each markerData as data, index (data.index)}
		<ButtonMarker
			lngLat={[data.position.lng, data.position.lat]}
			markerOptions={{ anchor: data.anchor }}
			class="rd-1"
			ariaLabel={data.ariaLabel}
			dataTestId={selectedRoute === index ? 'polyline-selected' : 'polyline-unselected'}
			onclick={() => onclick?.(index)}
		>
			<RouteMarkerContent
				hours={data.hours}
				minutes={data.minutes}
				totalCost={data.totalCost}
				borderRadiusPosition={data.borderRadiusPosition}
				selected={selectedRoute === index}
			/>
		</ButtonMarker>
	{/each}
{/if}
